import { useEffect, useRef, useState, type ReactNode } from 'react'
import { BookOpen, ChevronsLeft, ChevronsRight, Clock, Maximize2, Sparkles } from 'lucide-react'
import { Link, useNavigate } from 'react-router-dom'

import {
  getPersistedSidebarWidth,
  setPersistedSidebarWidth
} from '@axilon-apps/domains-ontology-ui-state'

import {
  consumeRailExpandOnNextMount,
  peekRailExpandOnNextMount,
  rememberMemoryReturnPath
} from '../Chat/chat-handoff'
import {
  CHAT_SHARE_ALLOWED_ACCESS_LEVELS,
  CHAT_SHARE_SCOPE_DESCRIPTION
} from '../Chat/chat-behavior'
import { CHAT_MEMORY_PATH, chatFullPagePath } from '../Chat/chat-route'
import { cn } from '../../lib/utils'
import { BREAKPOINT_QUERIES, useMediaQuery } from '../../lib/use-media-query'
import { designPixelsToRem, screenPixelsToDesignPixels } from '../../lib/ui-density'
import { Button } from '../ui/button'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip'
import { TabStrip, type SidebarTabDescriptor } from '../Chat/TabStrip'
import { ResourceShareButton } from '../ResourceSharingDialog'

const MIN_SIDEBAR_WIDTH = 280
const MAX_SIDEBAR_WIDTH = 720
const DEFAULT_SIDEBAR_WIDTH = 420

function clampSidebarWidth(value: number): number {
  return Math.min(MAX_SIDEBAR_WIDTH, Math.max(MIN_SIDEBAR_WIDTH, value))
}

/**
 * The chat sidebar is dock-able into the page grid only at xl+ where
 * there's enough horizontal room to keep the main content usable. Below
 * `xl` we render it as a floating overlay so the user can still pull it
 * up without squeezing the page contents into a column too narrow for
 * the toolbar buttons and split panes that most ontology pages use.
 */
function shouldDefaultExpanded(): boolean {
  if (peekRailExpandOnNextMount()) return true
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return true
  }
  return window.matchMedia(BREAKPOINT_QUERIES.xl).matches
}

interface SidebarShellProps {
  tabs: SidebarTabDescriptor[]
  activeTabId: string | null
  /**
   * Conversation the chat page should open. Tracks the orchestrator rather than
   * `activeTabId`, which is the ingest session while Current Ingest is focused.
   */
  chatTabId: string | null
  onSwitchTab: (id: string) => void
  onCloseTab: (id: string) => void
  onNewTab: () => void
  onSelectConversations?: () => void
  onExpanded: () => Promise<void>
  forceExpandKey?: number
  /** Share the focused chat thread without opening conversation history. */
  shareConversation?: { resourceName: string; accessScopeId: string }
  renderPane: (showHistory: boolean, closeHistory: () => void) => ReactNode
}

export function SidebarShell({
  tabs,
  activeTabId,
  chatTabId,
  onSwitchTab,
  onCloseTab,
  onNewTab,
  onSelectConversations,
  onExpanded,
  forceExpandKey,
  shareConversation,
  renderPane
}: SidebarShellProps) {
  // Default to collapsed on viewports narrower than `xl` (1280px) so the
  // 420 px chat panel doesn't immediately steal half the page width on a
  // typical 13" laptop. Above `xl`, default to expanded as before.
  const [expanded, setExpanded] = useState<boolean>(() => shouldDefaultExpanded())
  const [showHistory, setShowHistory] = useState(false)
  // Hydrate from localStorage on first render so refresh preserves the
  // user's drag-resized width. Falls back to the default when nothing is
  // stored (or storage is unavailable).
  const [width, setWidth] = useState<number>(() => {
    const stored = getPersistedSidebarWidth()
    return stored !== null ? clampSidebarWidth(stored) : DEFAULT_SIDEBAR_WIDTH
  })
  const [isResizing, setIsResizing] = useState(false)
  const resizeStartRef = useRef({ x: 0, width: DEFAULT_SIDEBAR_WIDTH })
  const lastForceExpandKeyRef = useRef(forceExpandKey)
  const navigate = useNavigate()

  useEffect(() => {
    consumeRailExpandOnNextMount()
  }, [])

  // Track the two breakpoints that drive the docked-vs-overlay decision.
  const isXl = useMediaQuery(BREAKPOINT_QUERIES.xl)
  const isMd = useMediaQuery(BREAKPOINT_QUERIES.md)
  const [viewportWidth, setViewportWidth] = useState<number>(() =>
    typeof window === 'undefined' ? Number.POSITIVE_INFINITY : window.innerWidth
  )
  useEffect(() => {
    if (typeof window === 'undefined') return
    const onResize = () => setViewportWidth(window.innerWidth)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  const overlayMode = !isXl
  const viewportWidthInDesignPixels = screenPixelsToDesignPixels(viewportWidth)
  const renderedWidth = overlayMode
    ? Math.min(
        width,
        Math.max(MIN_SIDEBAR_WIDTH, Math.floor(viewportWidthInDesignPixels * (isMd ? 0.6 : 0.92)))
      )
    : width

  useEffect(() => {
    setPersistedSidebarWidth(width)
  }, [width])

  useEffect(() => {
    if (!expanded) return
    void onExpanded().catch((err) => {
      console.warn('[global-query-sidebar] conversation-list load failed', err)
    })
  }, [expanded, onExpanded])

  useEffect(() => {
    if (forceExpandKey === lastForceExpandKeyRef.current) return
    lastForceExpandKeyRef.current = forceExpandKey
    if (forceExpandKey === undefined) return
    setShowHistory(false)
    setExpanded(true)
  }, [forceExpandKey])

  const handleResizePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    event.preventDefault()
    resizeStartRef.current = { x: event.clientX, width }
    setIsResizing(true)
    event.currentTarget.setPointerCapture(event.pointerId)
  }

  const handleResizePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!isResizing) return
    const delta = screenPixelsToDesignPixels(resizeStartRef.current.x - event.clientX)
    const ceiling = overlayMode
      ? Math.max(MIN_SIDEBAR_WIDTH, Math.floor(viewportWidthInDesignPixels * (isMd ? 0.6 : 0.92)))
      : MAX_SIDEBAR_WIDTH
    const next = Math.min(
      Math.min(MAX_SIDEBAR_WIDTH, ceiling),
      Math.max(MIN_SIDEBAR_WIDTH, resizeStartRef.current.width + delta)
    )
    setWidth(next)
  }

  const handleResizePointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!isResizing) return
    setIsResizing(false)
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }
  }

  // The chat conversation, not the focused rail tab: expanding while Current
  // Ingest is focused still lands on plant chat, which pins the run at the top
  // of its conversation list.
  const fullPagePath = chatFullPagePath(chatTabId)

  return (
    <aside
      aria-label="Ontology chat sidebar"
      style={expanded ? { width: designPixelsToRem(renderedWidth) } : undefined}
      className={cn(
        'z-30 h-screen shrink-0 overflow-hidden border-l bg-background',
        overlayMode ? 'fixed inset-y-0 right-0 shadow-lg' : 'relative sticky top-0',
        !isResizing && 'transition-[width] duration-700 ease-out',
        !expanded && 'w-12'
      )}
    >
      {expanded ? (
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize chat sidebar"
          className="group/resize absolute top-0 bottom-0 left-0 z-40 w-1.5 cursor-col-resize select-none"
          onPointerDown={handleResizePointerDown}
          onPointerMove={handleResizePointerMove}
          onPointerUp={handleResizePointerUp}
          onPointerCancel={handleResizePointerUp}
        >
          <div
            className={cn(
              'h-full w-full transition-colors',
              isResizing ? 'bg-border' : 'group-hover/resize:bg-border'
            )}
          />
        </div>
      ) : null}
      {!expanded ? (
        <button
          type="button"
          title="Open Industrial Agent"
          className="group flex h-full w-full cursor-pointer flex-col items-center justify-between py-4 hover:bg-accent focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-none"
          aria-label="Open Industrial Agent"
          onClick={() => {
            setShowHistory(false)
            setExpanded(true)
          }}
        >
          <span className="flex size-7 items-center justify-center rounded-md bg-primary text-primary-foreground shadow-sm">
            <Sparkles className="size-3.5" />
          </span>
          <span
            aria-hidden
            className="rotate-180 text-11 font-semibold tracking-[0.2em] text-muted-foreground uppercase transition-colors [writing-mode:vertical-rl] group-hover:text-foreground"
          >
            Ask agent
          </span>
          <ChevronsLeft className="size-4 text-muted-foreground transition-colors group-hover:text-foreground" />
        </button>
      ) : (
        <div className="flex h-full min-h-0 flex-col">
          <header className="border-b px-4 py-2">
            <div className="flex items-center gap-2">
              <Link
                to={fullPagePath}
                title="Open full page"
                className="flex min-w-0 flex-1 items-center gap-2 rounded-md text-inherit no-underline outline-none hover:opacity-90 focus-visible:ring-[3px] focus-visible:ring-ring/50"
              >
                <div className="flex size-7 shrink-0 items-center justify-center rounded-md bg-primary text-primary-foreground">
                  <Sparkles className="size-3.5" />
                </div>
                <h2 className="m-0 min-w-0 truncate text-sm font-semibold text-foreground">
                  Industrial Agent
                </h2>
              </Link>
              <TooltipProvider delayDuration={700}>
                {shareConversation ? (
                  <ResourceShareButton
                    resourceName={shareConversation.resourceName}
                    resourceLabel="conversation"
                    accessScopeId={shareConversation.accessScopeId}
                    allowedAccessLevels={CHAT_SHARE_ALLOWED_ACCESS_LEVELS}
                    scopeDescription={CHAT_SHARE_SCOPE_DESCRIPTION}
                    toolbar
                  />
                ) : null}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      aria-label="Agent memory"
                      onClick={() => {
                        rememberMemoryReturnPath(fullPagePath)
                        navigate(CHAT_MEMORY_PATH)
                      }}
                    >
                      <BookOpen className="size-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">Agent memory</TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      variant={showHistory ? 'secondary' : 'ghost'}
                      size="icon-sm"
                      aria-label={showHistory ? 'Show chat' : 'Show conversation history'}
                      onClick={() => setShowHistory((value) => !value)}
                    >
                      <Clock className="size-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">
                    {showHistory ? 'Show chat' : 'Conversation history'}
                  </TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      aria-label="Open chat full page"
                      onClick={() => navigate(fullPagePath)}
                    >
                      <Maximize2 className="size-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">Open full page</TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      aria-label="Collapse ontology chat sidebar"
                      onClick={() => setExpanded(false)}
                    >
                      <ChevronsRight className="size-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">Collapse sidebar</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </header>

          {showHistory ? (
            renderPane(true, () => setShowHistory(false))
          ) : (
            <>
              {tabs.length > 0 ? (
                <TabStrip
                  tabs={tabs}
                  activeTabId={activeTabId}
                  onSwitch={onSwitchTab}
                  onClose={onCloseTab}
                  onNew={onNewTab}
                  onSelectConversations={onSelectConversations}
                />
              ) : null}
              {renderPane(false, () => setShowHistory(false))}
            </>
          )}
        </div>
      )}
    </aside>
  )
}
